import Immutable from 'seamless-immutable';
import PropTypes from 'prop-types';
import { retrieveErrors } from '@widergy/web-utils/lib/message';
import { AlertHandler } from '@widergy/energy-ui';

import TipService from 'services/TipService';
import { message } from 'utils/stringUtils';

import { stringArrayToObject } from './utils';

/* ------------- Energy Tips actions' name ------------- */
export const actions = stringArrayToObject(
  [
    'GET_ENERGY_TIPS',
    'GET_ENERGY_TIPS_SUCCESS',
    'GET_ENERGY_TIPS_FAILURE',
    'GET_TIP',
    'GET_TIP_SUCCESS',
    'GET_TIP_FAILURE',
    'TIP_TOGGLE_LIKE',
    'TIP_TOGGLE_LIKE_SUCCESS',
    'TIP_TOGGLE_LIKE_FAILURE',
    'GET_PLACES',
    'GET_PLACES_SUCCESS',
    'GET_PLACES_FAILURE',
    'TIP_SHARE',
    'TIP_CLICKED'
  ],
  '@@TIP'
);

const privateActionCreators = {
  getEnergyTipsSuccess: tips => ({
    type: actions.GET_ENERGY_TIPS_SUCCESS,
    payload: tips,
    target: 'tips'
  }),
  getEnergyTipsFailure: error => ({
    type: actions.GET_ENERGY_TIPS_FAILURE,
    payload: { error },
    target: 'tips'
  }),
  tipToggleLikeSuccess: tip => ({
    type: actions.TIP_TOGGLE_LIKE_SUCCESS,
    payload: tip,
    target: 'tips'
  }),
  tipToggleLikeFailure: error => ({
    type: actions.TIP_TOGGLE_LIKE_FAILURE,
    payload: { error },
    target: 'tips'
  }),
  getPlacesSuccess: places => ({
    type: actions.GET_PLACES_SUCCESS,
    payload: places,
    target: 'places'
  }),
  getPlacesFailure: error => ({
    type: actions.GET_PLACES_FAILURE,
    payload: { error },
    target: 'places'
  })
};

export const actionCreators = {
  getEnergyTips(placeId) {
    return async dispatch => {
      dispatch({ type: actions.GET_ENERGY_TIPS, target: 'tips' });
      const response = await TipService.getTips(placeId);
      if (response.ok) {
        dispatch(privateActionCreators.getEnergyTipsSuccess(response.data));
      } else {
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:getEnergyTipsFailure')));
        dispatch(privateActionCreators.getEnergyTipsFailure(response.problem));
      }
    };
  },
  tipToggleLike(tip) {
    return async dispatch => {
      dispatch({ type: actions.TIP_TOGGLE_LIKE, target: 'tip' });
      const response = await TipService.tipToggleLike(tip);
      if (response.ok) {
        dispatch(privateActionCreators.tipToggleLikeSuccess(response.data));
      } else {
        AlertHandler.error(retrieveErrors(response.data, message('DefaultMessages:tipToggleLike')));
        dispatch(privateActionCreators.tipToggleLikeFailure(response.problem));
      }
    };
  },
  getPlaces() {
    return async dispatch => {
      dispatch({ type: actions.GET_PLACES, target: 'places' });
      const response = await TipService.getPlaces();
      if (response.ok) {
        dispatch(privateActionCreators.getPlacesSuccess(response.data));
      } else {
        // TODO: Add error message
        dispatch(privateActionCreators.getPlacesFailure(response.problem));
      }
    };
  },
  tipShare(tip) {
    return {
      type: actions.TIP_SHARE,
      payload: tip,
      target: 'tips'
    };
  },
  tipClicked(tip) {
    return {
      type: actions.TIP_CLICKED,
      payload: tip,
      target: 'tips'
    };
  }
};

/* ------------- Energy Tips reducer ------------- */
const defaultState = {
  tips: [],
  places: [],
  tipsLoading: true,
  placesLoading: false,
  tipsError: null,
  placesError: null
};

/* eslint-disable complexity */
export function reducer(state = Immutable(defaultState), action) {
  switch (action.type) {
    case actions.GET_ENERGY_TIPS:
    case actions.GET_TIP:
    case actions.TIP_TOGGLE_LIKE:
    case actions.GET_PLACES:
      return Immutable.merge(state, {
        [action.target]: defaultState[action.target],
        [`${action.target}Loading`]: true
      });
    case actions.GET_ENERGY_TIPS_SUCCESS:
    case actions.GET_TIP_SUCCESS:
    case actions.GET_PLACES_SUCCESS:
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [action.target]: action.payload,
        [`${action.target}Error`]: null
      });
    case actions.TIP_TOGGLE_LIKE_SUCCESS: {
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [action.target]: state[action.target].map(tip =>
          tip.id === action.payload.tip.id ? { ...tip, like: action.payload.like } : tip
        ),
        [`${action.target}Error`]: null
      });
    }
    case actions.GET_ENERGY_TIPS_FAILURE:
    case actions.GET_TIP_FAILURE:
    case actions.TIP_TOGGLE_LIKE_FAILURE:
    case actions.GET_PLACES_FAILURE:
      return Immutable.merge(state, {
        [`${action.target}Loading`]: false,
        [`${action.target}Error`]: { what: action.payload.error, where: action.type }
      });
    default:
      return state;
  }
}
/* eslint-enable complexity */

export const tipPropTypes = {
  description: PropTypes.string.isRequired,
  icon_thumb_url: PropTypes.string.isRequired,
  icon_url: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  like: PropTypes.bool.isRequired,
  place_id: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

export const placesPropTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  icon_url: PropTypes.string.isRequired,
  icon_thumb_url: PropTypes.string.isRequired,
  order: PropTypes.number.isRequired
};
